import { MoreHoriz } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { lazy, Suspense, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Can } from "../../permissions/Can";
import { JOB_ACTION_PERMISSIONS } from "../../permissions/configs/jobs/jobsActions";
import { resetJobCreateStepper } from "../../redux/features/jobCreateSlice";
import { selectActiveCompanyId } from "../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../redux/hooks/redux";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { ActionDropdownButton } from "../jobs/components/ActionDropdownButton";
import { colors, alphaVar } from "../ui/colors";
import { SASMenuStyles } from "../ui/styles/inputStyles";
import { JobsCreate } from "./JobCreate/Jobs.layout";
import { useJobCreateWindowStore } from "./JobCreate/store/useJobCreateWindowStore";

const JobsList = lazy(() => import("../jobs/jobs-list/JobsList"));

interface JobMenuActionsProps {
  clientUid?: string | null;
  variant?: "menu" | "dropdown";
  dropdownLabel?: string;
  hideSeeJobs?: boolean;
  dropdownProps?: {
    fullWidth?: boolean;
    maxWidth?: number | string;
  };
}

type HiddenTriggerButton = HTMLButtonElement | null;

export const JobMenuActions: React.FC<JobMenuActionsProps> = ({
  clientUid = null,
  variant = "menu",
  dropdownLabel,
  hideSeeJobs = false,
  dropdownProps,
}) => {
  const dispatch = useDispatch();
  const activeCompanyId = useAppSelector(selectActiveCompanyId);
  const openJobCreateWindow = useJobCreateWindowStore((store) => store.openWindow);
  const clearJobCreateWindow = useJobCreateWindowStore((store) => store.clearWindow);

  const seeTriggerRef = useRef<HiddenTriggerButton>(null);
  const assignTriggerRef = useRef<HiddenTriggerButton>(null);
  const addTriggerRef = useRef<HiddenTriggerButton>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<HTMLElement | null>(null);

  const menuOpen = Boolean(anchorEl);
  const subMenuOpen = Boolean(subMenuAnchorEl);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);

  const closeMenu = () => {
    setAnchorEl(null);
    setSubMenuAnchorEl(null);
  };

  const openSubMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setSubMenuAnchorEl(e.currentTarget);
  };

  const closeSubMenu = () => {
    setSubMenuAnchorEl(null);
  };

  const fire = (ref: React.RefObject<HiddenTriggerButton>) => {
    ref.current?.click?.();
    setTimeout(closeMenu, 0);
  };

  const prepareNewJob = () => {
    clearJobCreateWindow();
    dispatch(resetJobCreateStepper());
  };

  const onCloseModalRestart = prepareNewJob;

  const modalKeySuffix = clientUid ?? "all";
  const quoteModalKey = `Dashboard-JobsAdd-Business-${modalKeySuffix}`;
  const scratchModalKey = `Dashboard-JobsAdd-Business-Without-Quote-${modalKeySuffix}`;

  const createJobActions = [
    {
      label: "From a Quote Service",
      icon: <ChevronRightIcon fontSize="small" />,
      onClick: () => {
        prepareNewJob();
        openJobCreateWindow(quoteModalKey, activeCompanyId);
        fire(assignTriggerRef);
      },
    },
    {
      label: "From Scratch",
      icon: <AddIcon fontSize="small" />,
      onClick: () => {
        prepareNewJob();
        openJobCreateWindow(scratchModalKey, activeCompanyId);
        fire(addTriggerRef);
      },
    },
  ];

  return (
    <>
      {variant === "dropdown" ? (
        <ActionDropdownButton
          actionLabel={dropdownLabel || "Add New Job"}
          actions={createJobActions}
          fullWidth={dropdownProps?.fullWidth}
          maxWidth={dropdownProps?.maxWidth}
          buttonSx={{
            borderRadius: "6px",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        />
      ) : (
        <>
          <IconButton
            onClick={openMenu}
            sx={{
              color: colors.gray[300],
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: alphaVar(colors.orange.base, 0.08),
                color: "var(--fx-surface-text, #0F172A)",
              },
            }}
          >
            <MoreHoriz />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => {
              if (!subMenuOpen) closeMenu();
            }}
            sx={SASMenuStyles}
          >
            <Can feature={JOB_ACTION_PERMISSIONS.SEE_JOBS}>
              <MenuItem onClick={() => fire(seeTriggerRef)}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>See Jobs</ListItemText>
              </MenuItem>
            </Can>

            <Can feature={JOB_ACTION_PERMISSIONS.CREATE_JOBS}>
              <MenuItem
                onClick={openSubMenu}
                sx={{
                  "&:hover": {
                    backgroundColor: alphaVar(colors.orange.base, 0.08),
                  },
                }}
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Create Job</ListItemText>
              </MenuItem>
            </Can>
          </Menu>

          <Menu
            anchorEl={subMenuAnchorEl}
            open={subMenuOpen}
            onClose={closeSubMenu}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              ...SASMenuStyles,
              "& .MuiPaper-root": {
                ...SASMenuStyles?.["& .MuiPaper-root"],
                marginRight: "8px",
              },
            }}
          >
            <Can feature={JOB_ACTION_PERMISSIONS.CREATE_JOBS}>
              <Tooltip
                title="Create a Job from an existing Quote Service. The quote details will be used as the starting point."
                placement="left"
                arrow
              >
                <MenuItem
                  onClick={() => {
                    prepareNewJob();
                    openJobCreateWindow(quoteModalKey, activeCompanyId);
                    fire(assignTriggerRef);
                  }}
                >
                  <ListItemIcon>
                    <ChevronRightIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>From a Quote Service</ListItemText>
                </MenuItem>
              </Tooltip>
            </Can>

            <Can feature={JOB_ACTION_PERMISSIONS.CREATE_JOBS_WITHOUT_QUOTE}>
              <Tooltip
                title="Create a new Job from scratch without a quote. Choose between basic or advanced service options."
                placement="left"
                arrow
              >
                <MenuItem
                  onClick={() => {
                    prepareNewJob();
                    openJobCreateWindow(scratchModalKey, activeCompanyId);
                    fire(addTriggerRef);
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>From Scratch</ListItemText>
                </MenuItem>
              </Tooltip>
            </Can>
          </Menu>
        </>
      )}

      {!hideSeeJobs && (
        <BaseModalComponent
          modalLabel="My Jobs"
          modalKey={`Dashboard-Jobs-Business-${modalKeySuffix}`}
          elementTrigger={<button ref={seeTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
        >
          <Suspense fallback={<div>Loading...</div>}>
            <JobsList clientUid={clientUid} />
          </Suspense>
        </BaseModalComponent>
      )}

      <BaseModalComponent
        modalLabel="Assign Job To Quote"
        modalKey={quoteModalKey}
        onConfirmClose={onCloseModalRestart}
        elementTrigger={<button ref={assignTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        <JobsCreate withQuoteService clientUidSelected={clientUid} />
      </BaseModalComponent>

      <BaseModalComponent
        modalLabel="Add New Job"
        modalKey={scratchModalKey}
        onConfirmClose={onCloseModalRestart}
        elementTrigger={<button ref={addTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        <JobsCreate withQuoteService={false} clientUidSelected={clientUid} />
      </BaseModalComponent>
    </>
  );
};
